import { CAMERA, VIEW_H, VIEW_W } from '../config/config';
import { clamp, damp } from '../core/math';
import type { Rng } from '../core/rng';

/**
 * Side-scrolling camera. All entities live in world coordinates; the camera is the only
 * thing that converts them to screen space at draw time.
 */
export class Camera {
  x = 0;
  y = 0;
  /** Level bounds in pixels. */
  levelW = VIEW_W;
  levelH = VIEW_H;
  /** Classic behaviour: the view never scrolls back to the left. */
  allowBacktrack = false;
  /** Furthest-right position reached, used as the left clamp when backtracking is off. */
  minX = 0;
  shake = 0;
  shakeX = 0;
  shakeY = 0;

  constructor(private rng: Rng) {}

  reset(levelW: number, levelH: number, x = 0, y = 0): void {
    this.levelW = levelW;
    this.levelH = levelH;
    this.minX = 0;
    this.x = this.clampX(x);
    this.y = this.clampY(y);
    this.minX = this.x;
    this.shake = 0;
    this.shakeX = 0;
    this.shakeY = 0;
  }

  private clampX(x: number): number {
    const max = Math.max(0, this.levelW - VIEW_W);
    return clamp(x, this.allowBacktrack ? 0 : this.minX, max);
  }

  private clampY(y: number): number {
    const max = Math.max(0, this.levelH - VIEW_H);
    return clamp(y, 0, max);
  }

  /** Snap straight to the target (level start, respawn, warping between areas). */
  snapTo(cx: number, cy: number): void {
    this.minX = 0;
    this.x = this.clampX(cx - VIEW_W * CAMERA.anchorX);
    this.y = this.clampY(cy - VIEW_H * 0.5);
    this.minX = this.x;
  }

  follow(targetX: number, targetY: number, dt: number): void {
    // Horizontal: keep the player at the anchor, only pushing forward.
    const desiredX = targetX - VIEW_W * CAMERA.anchorX;
    this.x = this.clampX(damp(this.x, desiredX, CAMERA.lerpX, dt));
    if (!this.allowBacktrack) this.minX = Math.max(this.minX, this.x);

    // Vertical: dead zone in the middle of the screen so small hops don't move the view.
    const top = this.y + VIEW_H * CAMERA.deadZoneTop;
    const bottom = this.y + VIEW_H * CAMERA.deadZoneBottom;
    let desiredY = this.y;
    if (targetY < top) desiredY = this.y - (top - targetY);
    else if (targetY > bottom) desiredY = this.y + (targetY - bottom);
    this.y = this.clampY(damp(this.y, desiredY, CAMERA.lerpY, dt));
  }

  addShake(amount: number): void {
    this.shake = Math.min(CAMERA.maxShake, this.shake + amount);
  }

  updateShake(dt: number): void {
    if (this.shake <= 0.01) {
      this.shake = 0;
      this.shakeX = 0;
      this.shakeY = 0;
      return;
    }
    this.shake = Math.max(0, this.shake - CAMERA.shakeDecay * dt * this.shake);
    this.shakeX = this.rng.range(-this.shake, this.shake);
    this.shakeY = this.rng.range(-this.shake, this.shake) * 0.6;
  }

  /** Rounded render offset (keeps pixel art crisp). */
  get renderX(): number {
    return Math.round(this.x + this.shakeX);
  }

  get renderY(): number {
    return Math.round(this.y + this.shakeY);
  }

  get right(): number {
    return this.x + VIEW_W;
  }

  get bottom(): number {
    return this.y + VIEW_H;
  }

  /** Is this world-space box anywhere near the view? (used for culling / waking) */
  nearView(x: number, y: number, w: number, h: number, margin = 32): boolean {
    return (
      x + w > this.x - margin &&
      x < this.right + margin &&
      y + h > this.y - margin * 4 &&
      y < this.bottom + margin * 4
    );
  }
}
